import { Link } from "react-router-dom";
import { LoadingState } from "@/components/ui/system-state";
import { DENIED_REASON_CODES, UNAVAILABLE_REASON_CODES } from "@/features/admin/sso/sso-shared";
import { useLoadedPermission } from "@/hooks/use-permission";
import { useSsoLocale } from "@/i18n/use-sso-locale";
import { AdminSsoBreakGlassCard } from "@/features/admin/sso/break-glass-card";
import { AdminSsoComingNext } from "@/features/admin/sso/coming-next";
import { AdminSsoDomainsCard } from "@/features/admin/sso/domains-card";
import { AdminSsoProvidersCard } from "@/features/admin/sso/providers-card";
import { useSsoDomains } from "@/features/admin/sso/use-sso-domains";
import { useSsoProviders } from "@/features/admin/sso/use-sso-providers";

/**
 * Admin → Single sign-on (ALL-4870, SPEC-SSO-7 §2; page-spec `admin-sso`).
 *
 * Gates on the loaded effective-access contract, not on the static role table:
 * `useLoadedPermission` fails closed until GET /v1/session/effective-access has
 * answered, so the card never fires a request before the session's real scope
 * and permission grants are known. The permission is "sso:manage"
 * (auth.identity_provider.manage, mapped from AuthScope.
 * IDENTITY_PROVIDERS_MANAGE) — the same scope the gateway requires for both the
 * read and the writes, because SPEC-SSO-3 §1's separate `identity_providers:read`
 * scope does not exist yet. The API stays the enforcement point either way.
 *
 * The page owns the two reads and hands them down, so one render issues one
 * GET /v1/admin/sso/domains (shared by the domains table, the provider form's
 * verified-domain picker and the enable dialog) and one GET /v1/admin/sso
 * (whose `providers` and `breakGlass` feed the providers card and the
 * break-glass card's live summary). `useSsoBreakGlass` inside the break-glass
 * card issues its own GET /v1/admin/sso/break-glass, because that route is what
 * PUT replaces as a full set.
 */
export function AdminSso() {
  const catalog = useSsoLocale();
  const ui = catalog.ui;
  const { loaded, allowed } = useLoadedPermission("sso:manage");
  const domainsState = useSsoDomains(allowed);
  const providersState = useSsoProviders(allowed);

  /**
   * The domains card owns the page-level denied/unavailable state, and the
   * providers and break-glass cards must not paint a second copy of it.
   *
   * All three reads answer from the same admin surface, so "you may not read
   * this" and "this route is not served here" are facts about the PAGE, not
   * about one card. Each card stating it individually put three identical
   * "Access required" / "Single sign-on is not available in this environment
   * yet" panels on one screen, which reads as three separate problems and makes
   * every query for that state ambiguous. The card that carries the most
   * specific explanation (which permission, which support reference) states it
   * once; the other two render nothing.
   */
  const pageStateOwned =
    !allowed ||
    (domainsState.error !== null &&
      (DENIED_REASON_CODES.includes(domainsState.error.reasonCode) ||
        UNAVAILABLE_REASON_CODES.includes(domainsState.error.reasonCode)));

  return (
    <div dir={catalog.dir}>
      {/* Three items (Admin link, separator, current page) in a non-wrapping
          row: a long localized "current page" label would push the trail past
          a 375px viewport, so the row wraps instead. */}
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="inline-flex min-h-[36px] items-center text-primary no-underline hover:underline">
          {ui["sso.breadcrumbAdmin"]}
        </Link>
        <span className="text-muted" aria-hidden>
          ›
        </span>
        <span className="font-medium text-text">{ui["sso.breadcrumbCurrent"]}</span>
      </nav>

      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">{ui["sso.title"]}</h1>
        <p className="mt-1 max-w-[720px] text-sm text-muted">{ui["sso.subtitle"]}</p>
      </div>

      {loaded ? (
        <>
          <AdminSsoDomainsCard canManage={allowed} catalog={catalog} domainsState={domainsState} />
          <AdminSsoProvidersCard
            canManage={allowed}
            catalog={catalog}
            state={providersState}
            domainsState={domainsState}
            pageStateOwned={pageStateOwned}
          />
          <AdminSsoBreakGlassCard
            canManage={allowed}
            catalog={catalog}
            summary={providersState.breakGlass}
            pageStateOwned={pageStateOwned}
          />
        </>
      ) : (
        <LoadingState rows={3} />
      )}

      <AdminSsoComingNext catalog={catalog} />
    </div>
  );
}
